import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { FiPlus, FiCalendar, FiUsers, FiEdit2, FiTrash2, FiEye } from 'react-icons/fi'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import api, { fetcher } from '../../utils/api'
import Spinner from '../../components/Spinner'
import DashboardHeader from '../../components/DashboardHeader'
import ActivityForm from './ActivityForm'
import ActivityDetail from './ActivityDetail'
import FarmerActivityModal from './FarmerActivityModal'
import DeleteModal from '../../components/DeleteModal'
import FilterWithSearchAndRegion from '../../components/Filters/FilterWithSearchAndRegion'
import { Tooltip } from '../../layout/sidebar'
import { ErrorNotification } from '../../utils'
import { canModifyActivity, getActivityStatusBadgeClass } from '../../utils/activityUtils'

const ActivitiesView = () => {
  const history = useHistory()
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [showFarmerModal, setShowFarmerModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterCommodity, setFilterCommodity] = useState('all')
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Location filtering state
  const initialState = {
    region: "",
    district: "",
    community: "",
  }
  const [states, setState] = useState(initialState)
  const [filteredActivities, setFilteredActivities] = useState([])
  const [isFiltered, setIsFiltered] = useState(false)

  // Fetch activities, farmers, and commodities using SWR
  const { data: activities, isLoading: activitiesLoading, error: activitiesError, mutate: mutateActivities } = useSWR('activities/', fetcher)
  const { data: farmers, isLoading: farmersLoading } = useSWR('farmers/', fetcher)
  const { data: commodities, isLoading: commoditiesLoading } = useSWR('commodities/', fetcher)

  const loading = activitiesLoading

  // Filter activities based on search and filters
  const filterActivities = (searchTermParam = searchTerm) => {
    let filteredData = activities || []

    // Filter by search term
    if (searchTermParam && searchTermParam.trim()) {
      filteredData = filteredData.filter(activity =>
        activity?.name?.toLowerCase()?.includes(searchTermParam?.toLowerCase()) ||
        activity?.description?.toLowerCase()?.includes(searchTermParam?.toLowerCase())
      )
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filteredData = filteredData.filter(activity =>
        activity?.status === filterStatus
      )
    }

    // Filter by commodity
    if (filterCommodity !== 'all') {
      filteredData = filteredData.filter(activity =>
        activity?.commodities?.some(commodity => commodity.name === filterCommodity)
      )
    }

    // Filter by region
    if (states.region && states.region !== "Select Region") {
      filteredData = filteredData.filter(activity =>
        activity?.region?.toLowerCase()?.includes(states.region?.toLowerCase())
      )
    }

    // Filter by district
    if (states.district && states.district !== "Select District") {
      filteredData = filteredData.filter(activity =>
        activity?.district?.toLowerCase()?.includes(states.district?.toLowerCase())
      )
    }

    // Filter by community
    if (states.community && states.community !== "Select Community") {
      filteredData = filteredData.filter(activity =>
        activity?.community?.toLowerCase()?.includes(states.community?.toLowerCase())
      )
    }

    setFilteredActivities(filteredData)
    setIsFiltered(true)
  }

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('')
    setFilterStatus('all')
    setFilterCommodity('all')
    setState(initialState)
    setFilteredActivities([])
    setIsFiltered(false)
  }

  // Update filtered activities when dependencies change
  useEffect(() => {
    if (activities) {
      filterActivities()
    }
  }, [activities, searchTerm, filterStatus, filterCommodity, states])

  // Get activities to display
  const activitiesToShow = isFiltered ? filteredActivities : activities || []

  const handleCreateActivity = () => {
    setSelectedActivity(null)
    setShowForm(true)
  }

  const handleEditActivity = (activity) => {
    if (!canModifyActivity(activity)) {
      toast.warning('This activity has ended and can no longer be edited.')
      return
    }
    setSelectedActivity(activity)
    setShowForm(true)
  }

  const handleViewActivity = (activity) => {
    setSelectedActivity(activity)
    setShowDetail(true)
  }

  const handleManageFarmers = (activity) => {
    if (!canModifyActivity(activity)) {
      toast.warning('This activity has ended. Farmer assignments can no longer be changed.')
      return
    }
    setSelectedActivity(activity)
    setShowFarmerModal(true)
  }

  const handleDeleteActivity = async () => {
    setDeleteLoading(true)
    try {
      await api.delete(`activities/${selectedActivity.id}/`)
      toast.success('Activity deleted successfully')
      mutateActivities()
      setShowConfirmModal(false)
    } catch (error) {
      ErrorNotification(error.response.data || error.message || 'Failed to delete activity')
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleFormSubmit = () => {
    setShowForm(false)
    setSelectedActivity(null)
    mutateActivities()
  }

  const handleFarmerModalClose = () => {
    setShowFarmerModal(false)
    setSelectedActivity(null)
    mutateActivities()
  }

  const handleChange = (evt) => {
    const value = evt.target.value
    const name = evt.target.name
    setState({
      ...states,
      [name]: value,
    })
  }

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (activitiesError) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load activities</p>
          <button
            onClick={() => mutateActivities()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full lg:px-10 px-2 h-full py-10">
      <div className="w-full space-y-6">
        {/* DASHBOARD HEADER */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
          <DashboardHeader label="Activities" />
          <button
            onClick={handleCreateActivity}
            className="flex items-center px-4 py-2 bg-yellowcolor text-black rounded-lg hover:bg-yellowcolor/90 transition-colors"
          >
            <FiPlus className="mr-2" />
            Create Activity
          </button>
        </div>

        {/* FILTER SECTION */}
        <div className="bg-white dark:bg-dark p-4 rounded-lg border border-gray-200 dark:border-border-dark">
          <div className="space-y-4">
            {/* Main Filter Component */}
            <FilterWithSearchAndRegion
              placeholder="Search activities by name or description"
              suggestions={activities?.map(activity => activity?.name) || []}
              onSearch={(searchTerm) => {
                setSearchTerm(searchTerm)
                filterActivities(searchTerm)
              }}
              handleChange={handleChange}
              hideMap={true}
              setSearch={setSearchTerm}
              search={searchTerm}
            />

            {/* Additional Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="py-3 px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="planned">Planned</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {/* Commodity Filter */}
              <select
                value={filterCommodity}
                onChange={(e) => setFilterCommodity(e.target.value)}
                className="py-3 px-4 pe-9 block w-full border-gray-200 rounded-lg text-sm focus:border-yellowcolor focus:ring-yellowcolor disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-dark border dark:border-border-dark dark:text-white"
              >
                <option value="all">All Commodities</option>
                {commodities?.map(commodity => (
                  <option key={commodity?.id} value={commodity?.name}>
                    {commodity?.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Reset Filters Button */}
            {(searchTerm || filterStatus !== 'all' || filterCommodity !== 'all' || states.region || states.district || states.community) && (
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Activities Table */}
        <div className="my-5">
          {activitiesToShow.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-dark rounded-lg border border-gray-200 dark:border-border-dark">
              <FiCalendar className="mx-auto text-5xl text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No activities found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm || filterStatus !== 'all' || filterCommodity !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Get started by creating your first activity'}
              </p>
              {!searchTerm && filterStatus === 'all' && filterCommodity === 'all' && (
                <button
                  onClick={handleCreateActivity}
                  className="px-4 py-2 bg-yellowcolor text-black rounded-lg hover:bg-yellowcolor/90 transition-colors"
                >
                  Create Activity
                </button>
              )}
            </div>
          ) : (
            <div className="-m-1.5 overflow-x-auto">
              <div className="p-1.5 min-w-full inline-block align-middle">
                <div className="bg-white dark:bg-dark border dark:border-border-dark rounded-lg shadow-sm overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                    <thead className="bg-transparent dark:bg-secondaryDark">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Name</th>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Type</th>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Status</th>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Start Date</th>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">End Date</th>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Location</th>
                        <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Farmers</th>
                        <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                      {activitiesToShow.map((activity) => (
                        <tr
                          key={activity?.id}
                          className="text-[13px] hover:bg-gray-50 dark:hover:bg-secondaryDark/50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap font-light dark:text-white max-w-[200px]">
                            <div className="font-medium truncate">{activity?.name}</div>
                            {activity?.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                                {activity.description}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-light dark:text-white capitalize">
                            {activity?.activity_type_display || activity?.activity_type || 'Other'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-light">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActivityStatusBadgeClass(activity?.status)}`}>
                              {activity?.status_display || activity?.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-light dark:text-white">
                            {activity?.start_date ? new Date(activity.start_date).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-light dark:text-white">
                            {activity?.end_date ? new Date(activity.end_date).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-light dark:text-white">
                            <div>{activity?.district || '—'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {[activity?.community, activity?.region].filter(Boolean).join(', ')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-light dark:text-white">
                            {activity?.farmers_count || activity?.farmers?.length || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                            <div className="flex flex-row items-center justify-end gap-3">
                              <Tooltip text="View details" position="top">
                                <button type="button" onClick={() => handleViewActivity(activity)}>
                                  <FiEye size={16} className='dark:text-white text-black' />
                                </button>
                              </Tooltip>
                              {canModifyActivity(activity) && (
                                <>
                                  <Tooltip text="Manage farmers" position="top">
                                    <button
                                      type="button"
                                      onClick={() => handleManageFarmers(activity)}
                                      className="text-green-600"
                                    >
                                      <FiUsers size={16} />
                                    </button>
                                  </Tooltip>
                                  <Tooltip text="Edit" position="top">
                                    <button type="button" onClick={() => handleEditActivity(activity)}>
                                      <FiEdit2 size={16} />
                                    </button>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip text="Delete" position="top">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedActivity(activity)
                                    setShowConfirmModal(true)
                                  }}
                                  className="text-red-600"
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showForm && (
        <ActivityForm
          activity={selectedActivity}
          onClose={() => {
            setShowForm(false)
            setSelectedActivity(null)
          }}
          onSubmit={handleFormSubmit}
        />
      )}

      {showDetail && selectedActivity && (
        <ActivityDetail
          activity={selectedActivity}
          onClose={() => {
            setShowDetail(false)
            setSelectedActivity(null)
          }}
          onEdit={() => {
            setShowDetail(false)
            handleEditActivity(selectedActivity)
          }}
          onManageFarmers={() => {
            setShowDetail(false)
            handleManageFarmers(selectedActivity)
          }}
        />
      )}

      {showFarmerModal && selectedActivity && (
        <FarmerActivityModal
          activity={selectedActivity}
          onClose={handleFarmerModalClose}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmModal && selectedActivity && (
        <DeleteModal
          setShowConfirmModal={setShowConfirmModal}
          showConfirmModal={showConfirmModal}
          title="activity"
          loading={deleteLoading}
          handleDelete={handleDeleteActivity}
        />
      )}
    </div>
  )
}

export default ActivitiesView
